import { useState, type FormEvent } from 'react';

interface ComparisonCategorySectionProps {
  /** 是否同時需要「主要服務城市／行政區」（Service Area Business 且尚未提供 hint） */
  showServiceAreaField: boolean;
  /** 帶入的預設服務區域（例如使用者第一階段輸入的城市） */
  defaultServiceAreaHint?: string;
  /** 是否正在處理 */
  loading: boolean;
  /** 送出：使用者確認的同業分類（必填），服務區域（視情境） */
  onSubmit: (payload: { comparisonKeyword: string; serviceAreaHint?: string }) => void;
}

const KEYWORD_MIN = 2;
const KEYWORD_MAX = 40;

/**
 * 同業分類決策層回傳 USER_CONFIRMATION_REQUIRED 時顯示。
 * 這不是系統錯誤（用提示型黃色區塊），而是請使用者確認較精確的同業分類，
 * 避免把不同行業放在一起比較。Service Area Business 若同時缺範圍，會一併顯示兩個必要欄位。
 */
export default function ComparisonCategorySection({
  showServiceAreaField,
  defaultServiceAreaHint = '',
  loading,
  onSubmit,
}: ComparisonCategorySectionProps) {
  const [keyword, setKeyword] = useState('');
  const [hint, setHint] = useState(defaultServiceAreaHint);
  const [error, setError] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword.length < KEYWORD_MIN) {
      setError(`請輸入主要同業分類（至少 ${KEYWORD_MIN} 個字）。`);
      return;
    }
    if (trimmedKeyword.length > KEYWORD_MAX) {
      setError(`同業分類請勿超過 ${KEYWORD_MAX} 個字。`);
      return;
    }

    const trimmedHint = hint.trim();
    if (showServiceAreaField && trimmedHint.length < 2) {
      setError('請輸入主要服務城市或行政區，例如：台中、西屯區。');
      return;
    }

    setError('');
    onSubmit({
      comparisonKeyword: trimmedKeyword,
      serviceAreaHint: showServiceAreaField ? trimmedHint : undefined,
    });
  };

  return (
    <section className="ds-container pt-6">
      <div className="rounded-2xl border border-accent-200 bg-accent-50/70 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50 text-accent-700 ring-1 ring-accent-200">
            <i className="ri-questionnaire-line text-xl" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-accent-900">確認主要同業分類</p>
            <p className="mt-1.5 text-base text-accent-900/90">
              Google 公開商家類別較廣泛，為避免把不同行業放在一起比較，請輸入這家商家最主要想比較的同業分類。
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-4 space-y-4">
              <div>
                <label htmlFor="comparisonKeyword" className="mb-2 block text-base font-semibold text-accent-900">
                  主要同業分類
                </label>
                <div className="flex items-center gap-3 rounded-xl border border-accent-300 bg-background-50 px-4 py-3.5 transition-colors focus-within:border-accent-500">
                  <i className="ri-price-tag-3-line text-xl text-accent-500" />
                  <input
                    id="comparisonKeyword"
                    type="text"
                    name="comparisonKeyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    maxLength={KEYWORD_MAX}
                    placeholder="例如：心理諮商、整合行銷顧問、餐飲創業教學"
                    disabled={loading}
                    className="w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </div>
              </div>

              {showServiceAreaField && (
                <div>
                  <label htmlFor="serviceAreaHint" className="mb-2 block text-base font-semibold text-accent-900">
                    主要服務城市／行政區
                  </label>
                  <div className="flex items-center gap-3 rounded-xl border border-accent-300 bg-background-50 px-4 py-3.5 transition-colors focus-within:border-accent-500">
                    <i className="ri-map-pin-line text-xl text-accent-500" />
                    <input
                      id="serviceAreaHint"
                      type="text"
                      name="serviceAreaHint"
                      value={hint}
                      onChange={(e) => setHint(e.target.value)}
                      placeholder="例如：台中、西屯區、高雄左營"
                      disabled={loading}
                      className="w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-14 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    處理中…
                  </>
                ) : (
                  <>
                    <i className="ri-check-line text-lg" />
                    確認同業分類
                  </>
                )}
              </button>
            </form>

            {error && (
              <p className="mt-3 flex items-center gap-2 text-base text-rose-600">
                <i className="ri-error-warning-line text-lg" />
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}